<template>
    <div class="section section-post bg-grey">
        <div class="container">
            <Breadcrumb>
                <BreadcrumbItem :title="$t('bills.titles')" :to="{name: 'bills.index'}" />
            </Breadcrumb>
            <div class="section-header">
                <div class="row align-items-center justify-content-between">
                    <div class="col-md-9">
                        <h2>{{getLocaleText(post.zpNameKyr, post.zpNameRus)}}</h2>
                    </div>
                    <div class="col-md-3">
                        <PageAction/>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div class="section section-post section-bills section-bill">
        <div class="container">
            <div class="row">
                <div class="col-md-12">
                    <div class="section-body overflow-x-auto">
                        <table class="table">
                            <tbody>
                                <tr>
                                    <th scope="row">{{$t('bills.registration_number')}}</th>
                                    <td>{{ post.vh_nom }}</td>
                                </tr>
                                <tr>
                                    <th scope="row">{{$t('bills.registration_date')}}</th>
                                    <td>{{ getDate(post.vh_dat) }}</td>
                                </tr>
                                <tr>
                                    <th scope="row">{{$t('bills.subject_initiator')}}</th>
                                    <td>
                                        {{getLocaleText(post.zpInitiatorInfoKyr, post.zpInitiatorInfoRus)}}
                                    </td>
                                </tr>
                                <tr>
                                    <th scope="row">{{ $t('bills.speaker') }}</th>
                                    <td>{{post.adresat}}</td>
                                </tr>
                                <tr>
                                    <th scope="row">{{$t('bills.text')}}</th>
                                    <td>
                                        <a
                                            v-if="post.projectDoc"
                                           :href="getProjectDocUrl(post.projectDoc.parentid, 'kg')"
                                            class="d-flex align-items-start cursor-pointer">
                                            <PhFile/>
                                            {{$t('bills.project_doc_gov')}}
                                            ({{formatFileSize(post.projectDoc.file_size)}})
                                        </a>
                                        <a
                                            v-if="post.projectDoc"
                                            :href="getProjectDocUrl(post.projectDoc.parentid, 'ru')"
                                            class="d-flex align-items-start cursor-pointer">
                                            <PhFile/>
                                            {{$t('bills.project_doc_official')}}
                                            ({{formatFileSize(post.projectDoc.file_size)}})
                                        </a>
                                    </td>
                                </tr>
                                <tr>
                                    <th scope="row">{{ $t('bills.docs_full') }}</th>
                                    <td>
                                        <a  v-for="doc in post.accompdDocs"
                                            :href="getAccompDocUrl(doc.id)"
                                            class="d-flex align-items-start cursor-pointer">
                                            <PhFile/>
                                            {{doc.file_name}}
                                            ({{formatFileSize(doc.file_size)}})
                                        </a>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="col-md-12">
                    <div class="section-body mt-4">
                        <table class="table">
                            <tbody>
                            <tr>
                                <th scope="row" colspan="2">
                                    <h4>{{$t('bills.stages')}}</h4>
                                </th>
                            </tr>
                            <tr v-for="stage in post.stages">
                                <template v-if="stage.stageid === 1">
                                    <th scope="row">{{getStageName(stage.stageid)}}</th>
                                    <td>
                                        {{getResultName(stage.conclusionresultid)}}
                                        {{getDate(post.vh_dat)}}
                                    </td>
                                </template>
                                <template v-else>
                                    <th scope="row">{{ getStageName(stage.stageid) }}</th>
                                    <td>
                                        <div>
                                            <span v-if="stage.stageid % 2 === 0">{{ $t('bills.addressed') }}</span>
                                            <span v-else>{{$t('bills.agenda_include')}}</span>
                                            {{getDate(stage.deliverydate)}}
                                        </div>
                                        <div v-if="stage.conclusionresultid">
                                            {{getResultName(stage.conclusionresultid)}}
                                            {{getDate(stage.conclusiondate)}}
                                        </div>
                                    </td>
                                </template>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import "@/styles/pages/sections/posts.scss";
import "@/styles/pages/sections/post.scss";
import "@/styles/pages/sections/bills.scss";
import "@/styles/pages/sections/banners.scss";
import {PhFile} from "@phosphor-icons/vue";

import {onMounted, nextTick, reactive} from "vue";
import {useRoute, useRouter} from 'vue-router'
import fetchData from "@/js/global/fetchData";
import copyObject from "@/helpers/copyObject";
import getDate from "@/js/global/getDate";
import formatFileSize from "@/helpers/formatFileSize";
import getLocaleText from "@/js/global/getLocaleText";
import Trans from "@/i18n/translation";

const route = useRoute();
const router = useRouter();
const post = reactive({});
const stages = reactive([]);
const results = reactive([]);
const locale = Trans.currentLocale;


onMounted(async () => {
    await nextTick(); // Ожидаем, пока Vue завершит рендеринг
    await setPost(route.params.id);
})

async function setPost(postId) {
    const responseResults = await getResults();
    const responseStages = await getStages();
    const responsePost = await getPost(postId);

    results.push(...responseResults);
    stages.push(...responseStages);

    copyObject(responsePost, post);
}

async function getPost(id) {
    const url = SED_SERVER + "/docs/" + id;
    const response = await fetchData(url);

    if (!response) {
        return router.push(
            {name: 'notFound', params: {'catchAll': 'notFound'}}
        );
    }

    return response;
}

function getProjectDocUrl(id, lang) {
    return `${SED_SERVER}/docs/project/${id}?lang=${lang}`;
}

function getAccompDocUrl(id) {
    return `${SED_SERVER}/docs/accompdoc/${id}`;
}

function getStageName(id) {
    const stage = stages.find(item => item.id === id);

    if (!stage) return;

    if (locale === "kg") {
        return stage.name_kg;
    } else {
        return stage.name_ru;
    }
}

function getResultName(id) {
    const result = results.find(item => item.id === id);

    if (!result) return;

    if (locale === "kg") {
        return result.name_kg;
    } else {
        return result.name_ru;
    }
}

async function getStages() {
    const url = SED_SERVER + "/stages";
    return await fetchData(url);;
}

async function getResults() {
    const url = SED_SERVER + "/results";
    return await fetchData(url);;
}
</script>